// docs/34 SX11 — the sound of the place around a fight. Three beds on the ambience bus, each a pure
// function of the room (its land, hour, weather and what lies on the board), so a party of four
// hears one place:
//
//   the land     amb.<land> — the mire, a wood, a cave, a hall — its night band after dark
//   the sky      wx.rain / wx.storm / wx.blizzard / wx.wind / wx.fog, louder as the weather is heavier
//   the board    liquid.water / liquid.lava / liquid.fire, by how much of it there is
//   the life     amb.birds by day (a dawn chorus at first light), amb.crickets on a warm night,
//                amb.frogs over the mire after dark (docs/34 SX16)
//
// and moments on shared schedules: thunder after each lightning flash and gusts on a windy day
// (./storm.ts), and sparse accents of the place — a drip in a cave, a creak in a crypt, ice in the
// winter wood, stones trickling in the highlands. Every loop starts at the server clock's place in
// its file, and a bed's take inside its band is seeded, so a party hears one place.
import type { Snapshot } from '../sim/types';
import type { Weather } from '../../game/weather/weather';
import { stormEvery } from '../../game/weather/weather';
import { audio, setLoopGain, startLoop, stopLoop, type SfxKey } from './index';
import { hash32, seeded } from './mix';
import { nextStrike, setStorm, thunderOf } from './storm';
import { serverNow } from '../time';

/** `fk_sim::field::Land::of_kit`, for boards the module has not stamped with `env`. */
export function landOfKit(kit: string): string {
  switch (kit) {
    case 'mirewood': return 'Bog';
    case 'jungle': return 'Jungle';
    case 'underdark': return 'Underdark';
    case 'ashlands': return 'Ashlands';
    case 'forest': case 'autumn': case 'pinewood': return 'Wood';
    case 'winterwood': return 'Snowland';
    case 'dunes': return 'Dunes';
    case 'highland': return 'Highland';
    case 'cavern': case 'tomb': case 'mine': return 'Cave';
    case 'ember': return 'Ember';
    case 'keep': case 'cistern': case 'crypt': case 'sewer': case 'brick': case 'shrine': case 'temple': return 'Hall';
    // docs/187 PK2: the indoor places are halls; an outdoor place alone stands in a green wood (a fight's is stamped with its country's)
    case 'house': case 'ship': case 'barrow': return 'Hall';
    case 'town': case 'docks': case 'courtyard': case 'graveyard': case 'ruins': return 'Wood';
    default: return 'Plain';
  }
}

const OUTDOORS = new Set(['Bog', 'Wood', 'Snowland', 'Dunes', 'Highland', 'Jungle', 'Underdark', 'Ashlands']);
/** Under the rock (docs/23 Y9): its own weather, but no wind, thunder or birdsong. */
const UNDER = 'Underdark';
/** The Ashlands (docs/23 Y10): the fire under the ground, ash on the wind, the mountains' rumble, and nothing singing. */
const ASH = 'Ashlands';
/** Lands whose water is lava (`class_of`). */
const MOLTEN = new Set(['Ember', ASH]);

/** The land's bed, and whether it has a night band. */
function landBed(land: string, kit: string): { key: SfxKey; night: boolean } {
  if (kit === 'crypt' || kit === 'tomb') return { key: 'amb.crypt', night: false };
  switch (land) {
    case 'Bog': case 'Jungle': return { key: 'amb.bog', night: true };
    case 'Cave': case 'Underdark': return { key: 'amb.cave', night: false };
    case 'Ember': case ASH: return { key: 'amb.ember', night: false };
    case 'Hall': return { key: 'amb.hall', night: false };
    case 'Dunes': return { key: 'amb.dunes', night: false };
    case 'Highland': return { key: 'amb.highland', night: false };
    case 'Snowland': return { key: 'amb.snowland', night: false };
    default: return { key: 'amb.wood', night: true };
  }
}

export interface AmbiencePlan {
  beds: Array<{ id: string; key: SfxKey; gain: number; band?: number; variant?: number }>;
  storm?: { key: string; every: number };
  /** The gust schedule's key on a windy day. */
  gusts?: string;
  /** Sparse accents of the place: one-shots on a seeded schedule (docs/34 SX16). */
  accents?: Array<{ key: SfxKey; every: number; chance: number; gain: number; seed: string }>;
}

/** The take of `key` inside band `band` for this place: seeded, so every client plays the same one. */
function takeIn(key: SfxKey, band: number | undefined, seed: string): number | undefined {
  const b = audio.bank(key);
  if (!b?.variants.length) return undefined;
  const bd = band !== undefined && b.bands.length ? b.bands[band % b.bands.length] : undefined;
  const [start, count] = bd ? [bd.start, bd.count] : [0, b.variants.length];
  return start + (hash32(`${seed}|${key}|${band ?? ''}`) % Math.max(1, count));
}

/** Wind's two bands (sfx.banks.json `wx.wind`): over open country, and through trees. */
const WIND_OPEN = 0, WIND_TREES = 1;

/** The sky's beds over open country: rain, storm, blizzard, sand, fog, snow, and wind on a windy day. */
function skyBeds(beds: AmbiencePlan['beds'], weather: Weather, land: string): void {
  const k = Math.max(0, Math.min(1, weather.intensity));
  // under the rock the seep is drips (accentsFor), the fog a spore haze; no wind reaches it
  if (land === UNDER) {
    if (weather.kind === 'fog') beds.push({ id: 'sky', key: 'wx.fog', gain: 0.3 + 0.3 * k });
    return;
  }
  const wind = Math.abs(weather.wind);
  switch (weather.kind) {
    case 'rain': beds.push({ id: 'sky', key: 'wx.rain', gain: 0.35 + 0.65 * k }); break;
    case 'sleet': beds.push({ id: 'sky', key: 'wx.rain', gain: 0.3 + 0.4 * k }); break;
    case 'storm': beds.push({ id: 'sky', key: 'wx.storm', gain: 0.6 + 0.4 * k }, { id: 'sky2', key: 'wx.rain', gain: 0.5 + 0.4 * k }); break;
    case 'blizzard': beds.push({ id: 'sky', key: 'wx.blizzard', gain: 0.55 + 0.45 * k }); break;
    case 'sandstorm': beds.push({ id: 'sky', key: 'wx.wind', gain: 0.6 + 0.4 * k, band: WIND_OPEN }); break;
    case 'fog': beds.push({ id: 'sky', key: 'wx.fog', gain: 0.5 + 0.5 * k }); break;
    case 'snow': beds.push({ id: 'sky', key: 'wx.wind', gain: 0.18 + 0.2 * k, band: WIND_TREES }); break;
    default: break;
  }
  if (wind > 0.45 && !beds.some((b) => b.key === 'wx.wind') && weather.kind !== 'blizzard') beds.push({ id: 'wind', key: 'wx.wind', gain: Math.min(0.8, (wind - 0.3) * 0.9), band: land === 'Wood' || land === 'Jungle' ? WIND_TREES : WIND_OPEN });
}

const WET_SKY = new Set(['rain', 'storm', 'sleet', 'blizzard', 'sandstorm']);

/**
 * The life over the land (SX16): birdsong by day — loudest in the dawn chorus — where birds live and
 * the sky lets them sing, crickets on a night outside winter, frogs over the mire after dark.
 */
export function lifeBeds(beds: AmbiencePlan['beds'], land: string, hour: number, season: string | undefined, weather: Weather): void {
  if (!OUTDOORS.has(land) || land === 'Dunes' || land === UNDER || land === ASH) return;
  const winter = season === 'winter' || land === 'Snowland';
  const wet = WET_SKY.has(weather.kind);
  const night = hour < 5 || hour >= 21;
  const dawn = hour >= 5 && hour < 8;
  if (!night && !wet && !(winter && land !== 'Wood')) beds.push({ id: 'life', key: 'amb.birds', gain: dawn ? 0.75 : winter ? 0.25 : 0.45 });
  if (night && !winter && weather.kind !== 'storm') beds.push({ id: 'life', key: land === 'Bog' || land === 'Jungle' ? 'amb.frogs' : 'amb.crickets', gain: 0.55 });
}

/** The place's sparse accents (SX16): a cave's drips and falling grit, a hall's creaks, a crypt's voice in the dark, the winter wood's ice. */
export function accentsFor(land: string, kit: string, seed: string, weather?: Weather): NonNullable<AmbiencePlan['accents']> {
  const out: NonNullable<AmbiencePlan['accents']> = [];
  const a = (key: SfxKey, every: number, chance: number, gain: number) => out.push({ key, every, chance, gain, seed: `${seed}|${key}` });
  // the Underdark's seep (its "rain") drips twice as often
  if (land === UNDER) a('amb.drip', weather?.kind === 'rain' ? 2100 : 4200, 0.6, 0.7);
  else if (land === 'Cave' || kit === 'cistern' || kit === 'sewer') a('amb.drip', 4200, 0.6, 0.7);
  if (land === 'Cave' || land === 'Highland' || land === UNDER) a('amb.rumble', 23000, 0.35, 0.6);
  // the Ashlands' mountains grumble and the ground shakes, more often than any cave's
  if (land === ASH) a('amb.rumble', 13000, 0.5, 0.75);
  if (land === 'Hall' || kit === 'crypt' || kit === 'tomb') a('amb.creak', 15000, 0.4, 0.65);
  if (kit === 'crypt' || kit === 'tomb') a('amb.ghost', 41000, 0.3, 0.5);
  if (land === 'Snowland') a('amb.ice', 14000, 0.4, 0.6);
  return out;
}

/** What should be playing for this board (pure; the same on every client). */
/** docs/61 EX13: the bed each air (`fk_schema::Air` order) adds. */
const AIR_BEDS: ReadonlyArray<{ key: SfxKey; gain: number }> = [
  { key: 'amb.ghost', gain: 0.32 }, { key: 'amb.birds', gain: 0.5 }, { key: 'amb.creak', gain: 0.22 }, { key: 'amb.ghost', gain: 0.45 },
  { key: 'amb.birds', gain: 0.6 }, { key: 'amb.crickets', gain: 0.7 }, { key: 'amb.drip', gain: 0.6 }, { key: 'amb.ice', gain: 0.5 },
];

export function ambienceFor(snapshot: Snapshot, kit: string, hour: number, weather: Weather): AmbiencePlan {
  const board = snapshot.board;
  const land = board.env?.land ?? landOfKit(kit);
  const outdoors = OUTDOORS.has(land);
  const pick = (key: SfxKey) => { const n = audio.bank(key)?.variants.length ?? 1; return hash32(`${snapshot.encounterId}|${key}`) % Math.max(1, n); };
  const beds: AmbiencePlan['beds'] = [];
  const bed = landBed(land, kit);
  const night = hour < 6 || hour >= 20;
  const seed = `${snapshot.encounterId}`;
  if (bed.night) beds.push({ id: 'land', key: bed.key, gain: 1, band: night ? 1 : 0, variant: takeIn(bed.key, night ? 1 : 0, seed) });
  else beds.push({ id: 'land', key: bed.key, gain: 1, variant: pick(bed.key) });
  if (outdoors) skyBeds(beds, weather, land);
  lifeBeds(beds, land, hour, (board.env?.season ?? board.season)?.toLowerCase(), weather);
  for (const b of beds) if (b.id !== 'land' && b.variant === undefined) b.variant = takeIn(b.key, b.band, seed);
  const accents = accentsFor(land, kit, seed, weather);
  let water = 0, lava = 0, fire = 0;
  for (const t of board.tiles) {
    if (t.terrain === 'Water' || t.terrain === 'Deep') { if (MOLTEN.has(land)) lava++; else water++; }
    if (t.hazard === 'Fire' || t.hazard === 'Burning') fire++;
  }
  const amount = (n: number) => Math.min(1, 0.25 + n / 40);
  if (water) beds.push({ id: 'water', key: 'liquid.water', gain: amount(water) });
  if (lava) beds.push({ id: 'lava', key: 'liquid.lava', gain: amount(lava) });
  if (fire) beds.push({ id: 'fire', key: 'liquid.fire', gain: Math.min(1, 0.4 + fire / 8) });
  // docs/61 EX13: an exploration's air sounds under the land — a ghostly hush in the mist, birdsong
  // in a glimmer, crickets under the stars, drips in the echoing deep, ice that sings in the rime
  const air = snapshot.explore ? AIR_BEDS[snapshot.explore.air] : undefined;
  if (air) beds.push({ id: 'air', key: air.key, gain: air.gain, variant: takeIn(air.key, undefined, `${seed}|air`) });
  const wind = Math.abs(weather.wind);
  const open = outdoors && land !== UNDER;
  const every = open ? stormEvery(weather) : 0;
  return {
    beds,
    ...(every > 0 ? { storm: { key: `${snapshot.encounterId}`, every } } : {}),
    ...(open && (wind > 0.55 || weather.kind === 'sandstorm' || weather.kind === 'blizzard') ? { gusts: `${snapshot.encounterId}` } : {}),
    ...(accents.length ? { accents } : {}),
  };
}

/**
 * docs/36: the overworld's soundscape — the realm's country as it sounds now (its wood, mire,
 * dunes, highland or winter), the river under it unless frozen, and the week's weather over the
 * top with thunder and gusts on the shared storm schedule keyed by the World. There is no music
 * in the licensed library yet; this is the place's own sound, the same for everyone in the World.
 */
export function overworldAmbience(worldKey: string, biome: string | undefined, season: string | undefined, weather: Weather | undefined, frozen = false): AmbiencePlan {
  const winter = season === 'winter' && biome !== 'desert' && biome !== 'jungle' && biome !== 'underdark' && biome !== 'ashlands';
  const land = biome === 'underdark' ? UNDER : biome === 'ashlands' ? ASH : winter ? 'Snowland' : biome === 'desert' ? 'Dunes' : biome === 'highland' ? 'Highland' : biome === 'jungle' || biome === 'wetland' ? 'Bog' : 'Wood';
  const bed = landBed(land, '');
  const beds: AmbiencePlan['beds'] = [bed.night ? { id: 'land', key: bed.key, gain: 0.85, band: 0 } : { id: 'land', key: bed.key, gain: 0.85, variant: hash32(`${worldKey}|${bed.key}`) % Math.max(1, audio.bank(bed.key)?.variants.length ?? 1) }];
  if (land === ASH) beds.push({ id: 'lava', key: 'liquid.lava', gain: 0.22 });
  else if (!frozen && land !== 'Dunes') beds.push({ id: 'water', key: 'liquid.water', gain: 0.22 });
  const sky = weather ?? { kind: 'clear', intensity: 0, wind: 0 } as Weather;
  skyBeds(beds, sky, land);
  const open = land !== UNDER;
  const every = open ? stormEvery(sky) : 0;
  const wind = open ? Math.abs(sky.wind) : 0;
  return {
    beds,
    ...(every > 0 ? { storm: { key: `world-${worldKey}`, every } } : {}),
    ...(open && (wind > 0.55 || sky.kind === 'sandstorm' || sky.kind === 'blizzard') ? { gusts: `world-${worldKey}` } : {}),
  };
}

let playing = new Map<string, string>();   // bed id → `${key}#${variant|band}`
let thunderTimer: ReturnType<typeof setTimeout> | undefined;
let gustTimer: ReturnType<typeof setTimeout> | undefined;
let planKey = '';

function scheduleThunder(st: { key: string; every: number }): void {
  if (thunderTimer) clearTimeout(thunderTimer);
  const s = nextStrike(st.key, st.every, serverNow());
  if (!s) return;
  const t = thunderOf(s);
  const wait = s.at + t.delayMs - serverNow();
  thunderTimer = setTimeout(() => {
    const n = audio.bank('wx.thunder')?.variants.length ?? 0;
    if (n) audio.play('wx.thunder', { variant: s.seed % n, gain: t.gain, rate: t.rate, origin: 'server', maxLateMs: 400 });
    scheduleThunder(st);
  }, Math.max(0, wait));
}

const GUST_SLOT = 9000;
function scheduleGusts(key: string): void {
  if (gustTimer) clearTimeout(gustTimer);
  const now = serverNow();
  let slot = Math.floor(now / GUST_SLOT);
  for (;;) {
    const r = seeded(hash32(`${key}|gust|${slot}`));
    const hit = r() < 0.55;
    const at = slot * GUST_SLOT + GUST_SLOT * r();
    if (hit && at > now) {
      gustTimer = setTimeout(() => {
        const n = audio.bank('wx.gust')?.variants.length ?? 0;
        if (n) audio.play('wx.gust', { variant: Math.floor(r() * n), gain: 0.6 + 0.4 * r(), pan: r() * 1.2 - 0.6, origin: 'server', maxLateMs: 400 });
        scheduleGusts(key);
      }, at - now);
      return;
    }
    slot++;
  }
}

let accentTimers: Array<ReturnType<typeof setTimeout>> = [];
/** One accent's next hit on its seeded schedule (a slot of `every` ms, a chance, an offset). */
export function nextAccent(seed: string, every: number, chance: number, now: number): { at: number; r: () => number } {
  for (let slot = Math.floor(now / every); ; slot++) {
    const r = seeded(hash32(`${seed}|${slot}`));
    const hit = r() < chance;
    const at = slot * every + every * r();
    if (hit && at > now) return { at, r };
  }
}
function scheduleAccent(a: NonNullable<AmbiencePlan['accents']>[number], i: number): void {
  const now = serverNow();
  const { at, r } = nextAccent(a.seed, a.every, a.chance, now);
  accentTimers[i] = setTimeout(() => {
    const n = audio.bank(a.key)?.variants.length ?? 0;
    if (n) audio.play(a.key, { variant: Math.floor(r() * n), gain: a.gain * (0.7 + 0.3 * r()), pan: r() * 1.4 - 0.7, origin: 'server', maxLateMs: 400 });
    scheduleAccent(a, i);
  }, at - now);
}

/** Plays the beds of `plan` (fading what changed), or stops everything for `undefined`. */
export function setAmbience(plan: AmbiencePlan | undefined): void {
  const key = plan ? JSON.stringify(plan) : '';
  if (key === planKey) return;
  planKey = key;
  const want = new Map((plan?.beds ?? []).map((b) => [b.id, b]));
  for (const [id] of playing) if (!want.has(id)) { stopLoop(`amb:${id}`, 2500); playing.delete(id); }
  for (const b of want.values()) {
    const sig = `${b.key}#${b.variant ?? ''}|${b.band ?? ''}`;
    if (playing.get(b.id) === sig) { setLoopGain(`amb:${b.id}`, b.gain, 1500); continue; }
    startLoop(`amb:${b.id}`, b.key, { gain: b.gain, fadeMs: 2500, ...(b.variant !== undefined ? { variant: b.variant } : {}), ...(b.band !== undefined ? { band: b.band } : {}) });
    playing.set(b.id, sig);
  }
  setStorm(plan?.storm);
  if (thunderTimer) { clearTimeout(thunderTimer); thunderTimer = undefined; }
  if (plan?.storm) scheduleThunder(plan.storm);
  if (gustTimer) { clearTimeout(gustTimer); gustTimer = undefined; }
  if (plan?.gusts) scheduleGusts(plan.gusts);
  for (const t of accentTimers) clearTimeout(t);
  accentTimers = [];
  (plan?.accents ?? []).forEach((a, i) => scheduleAccent(a, i));
  if (!plan) playing = new Map();
}
